import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import type { CSSProperties, FC, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { List, type ListImperativeAPI, type RowComponentProps } from 'react-window';

import { DEFAULT_ITEM_SIZE, DEFAULT_LIST_HEIGHT } from '../../constants';
import type { ListItemBase } from '../../types';

import { ListLoader } from './ListLoader';
import { StyledList } from './ListVirtualized.styled';

export type ListItemProps<T, P> = {
  index: number;
  item: T;
  style: CSSProperties;
} & P;

export type VirtualizedListProps<T, ListItemP> = {
  data: T[];
  emptyListText?: string;
  height?: number;
  isLoading?: boolean;
  itemSize?: (index: number) => number;
  ListItemComponent: FC<ListItemProps<T, ListItemP>>;
  listItemProps: ListItemP;
  virtualListRef?: Ref<ListImperativeAPI>;
};

type VirtualizedRowProps = {
  data: ListItemBase[];
  ListItemComponent: FC<ListItemProps<ListItemBase, Record<string, unknown>>>;
  listItemProps: Record<string, unknown>;
};

function VirtualizedRow({
  index,
  style,
  data,
  ListItemComponent,
  listItemProps,
}: RowComponentProps<VirtualizedRowProps>) {
  const item = data[index];

  return <ListItemComponent {...listItemProps} index={index} item={item} style={style} />;
}

const getDefaultItemSize = () => DEFAULT_ITEM_SIZE;

const getVirtualizedRowKey = (index: number, { data }: VirtualizedRowProps) => data[index].id;

export const ListVirtualized = <T extends ListItemBase, ListItemP extends Record<string, unknown>>(
  props: VirtualizedListProps<T, ListItemP>
) => {
  const { t } = useTranslation();

  const {
    data,
    emptyListText = t('No items.'),
    height = DEFAULT_LIST_HEIGHT,
    isLoading = false,
    itemSize = getDefaultItemSize,
    ListItemComponent,
    listItemProps,
    virtualListRef,
  } = props;

  if (isLoading) {
    return (
      <StyledList listHeight={height}>
        <ListLoader />
      </StyledList>
    );
  }

  if (!data.length) {
    return (
      <StyledList listHeight={height}>
        <ListItem>
          <ListItemText secondary={emptyListText} />
        </ListItem>
      </StyledList>
    );
  }

  return (
    <List
      listRef={virtualListRef}
      rowComponent={VirtualizedRow}
      rowCount={data.length}
      rowHeight={itemSize}
      rowKey={getVirtualizedRowKey}
      rowProps={{
        data,
        ListItemComponent: ListItemComponent as FC<ListItemProps<ListItemBase, Record<string, unknown>>>,
        listItemProps,
      }}
      style={{ height, width: '100%' }}
    />
  );
};
